import { html } from '../lib/html.js';
import { toastError } from '../lib/ui.js';
import { tabbarFooter } from '../lib/tabs.js';
import { openSpaceSheet, spaceButton } from '../lib/space-ui.js';
import { CATEGORY, CATEGORY_KEYS, CLAIM_STATUS, RELATION, categoryOf, meterOf, vehicleId, vehicleName, vehicles } from '../services/vehicles.js';
import { fmtKm } from '../services/maintenances.js';
import { Page, go } from './base.js';
import { pill } from './maintenance-ui.js';
import { catIcon } from './vehicle-hero.js';
import { nextShort } from './home.js';

/**
 * Garagem (aba do espaço Pessoal): todos os veículos, para quem tem um veículo ou uma frota.
 *   busca (placa, série, modelo ou apelido) · filtro por linha (só se houver mais de uma) ·
 *   grupos "Pedem atenção" (revisão vencida ou chegando), "Em dia" e "Documento em andamento".
 */
class GaragePage extends Page {
  template() {
    return html`
      <ion-content class="ion-padding tc-page root-page has-tabbar" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow">
          <div class="top-row">${'' /* seletor de espaço entra no onEnter */}</div>
          <div class="title-row">
            <h1 class="page-title">Garagem</h1>
            <button type="button" class="add-btn" data-go="/vehicles/new" aria-label="Adicionar veículo"><ion-icon name="add-outline"></ion-icon></button>
          </div>
          <label class="searchbar"><ion-icon name="search-outline" aria-hidden="true"></ion-icon><input type="search" placeholder="Placa, série, modelo ou apelido" aria-label="Buscar na garagem" enterkeyhint="search" /></label>
          <div class="filters"></div>
          <div class="body"></div>
        </div>
      </ion-content>
      ${tabbarFooter('garage')}`;
  }

  setup() {
    this.filter = 'ALL';
    this.term = '';
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="space"]')) return openSpaceSheet();
      const f = e.target.closest('[data-filter]');
      if (f) {
        this.filter = f.dataset.filter;
        return this.render();
      }
      const el = e.target.closest('[data-go]');
      if (el) go(el.dataset.go);
    });
    this.$('.searchbar input').addEventListener('input', (e) => {
      this.term = e.target.value.trim().toLocaleLowerCase('pt-BR');
      this.renderList();
    });
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
  }

  onEnter() {
    this.$('tc-tabbar')?.refresh();
    this.$('.top-row').innerHTML = spaceButton().value ?? '';
    this.load();
  }

  async load() {
    if (!this.items) this.$('.body').innerHTML = '<div class="center-small"><ion-spinner></ion-spinner></div>';
    try {
      this.items = (await vehicles.list()).items;
      this.error = null;
    } catch (err) {
      this.error = err.message;
      if (this.items) toastError(err.message);
    }
    this.render();
  }

  render() {
    const items = this.items ?? [];
    const counts = Object.fromEntries(CATEGORY_KEYS.map((k) => [k, items.filter((x) => categoryOf(x.vehicle).key === k).length]));
    const lines = CATEGORY_KEYS.filter((k) => counts[k]);
    if (lines.length < 2) this.filter = 'ALL';
    const chip = (key, label, icon = '') =>
      html`<button type="button" class="chip ${this.filter === key ? 'on' : ''}" data-filter="${key}" aria-pressed="${this.filter === key ? 'true' : 'false'}">${icon ? html`<ion-icon class="cat-color-${key}" name="${icon}" aria-hidden="true"></ion-icon>` : ''}${label}</button>`;
    this.$('.filters').innerHTML =
      lines.length > 1
        ? html`<div class="chip-row" role="group" aria-label="Filtrar por linha">${chip('ALL', `Todos · ${items.length}`)}${lines.map((k) => chip(k, `${CATEGORY[k].short} ${counts[k]}`, CATEGORY[k].icon))}</div>`.value
        : '';
    // busca só aparece com mais de 5 veículos (frota); com poucos, a lista já basta
    this.$('.searchbar').hidden = items.length <= 5;
    this.renderList();
  }

  renderList() {
    const body = this.$('.body');
    if (this.error && !this.items) {
      body.innerHTML = html`<div class="panel"><p class="panel-text">Não foi possível carregar seus veículos. Puxe para baixo para tentar de novo.</p></div>`.value;
      return;
    }
    const all = this.items ?? [];
    if (!all.length) {
      body.innerHTML = html`
        <div class="panel empty-panel" style="margin-top:16px">
          <div class="empty-badge"><ion-icon name="tc-garage"></ion-icon></div>
          <strong>Sua garagem está vazia</strong>
          <p>Adicione um veículo, caminhão ou máquina agrícola. O histórico fica com o veículo, e o app avisa antes de cada revisão.</p>
          <ion-button expand="block" class="empty-cta" data-go="/vehicles/new"><ion-icon slot="start" name="add-circle-outline"></ion-icon>Adicionar veículo</ion-button>
        </div>`.value;
      return;
    }
    const t = this.term;
    const list = all.filter((x) => (this.filter === 'ALL' || categoryOf(x.vehicle).key === this.filter) && (!t || searchText(x).includes(t)));
    const groups = [
      { label: 'Pedem atenção', items: list.filter((x) => x.access && ['OVERDUE', 'SOON'].includes(x.summary?.next?.status)) },
      { label: 'Em dia', items: list.filter((x) => x.access && !['OVERDUE', 'SOON'].includes(x.summary?.next?.status)) },
      { label: 'Documento em andamento', items: list.filter((x) => !x.access) },
    ].filter((g) => g.items.length);
    body.innerHTML = groups.length
      ? html`${groups.map((g) => html`<p class="group-label">${g.label}</p><div class="panel vlist">${g.items.map(row)}</div>`)}`.value
      : html`<p class="hint" style="margin:20px 4px">Nenhum veículo encontrado${t ? ` para “${this.term}”` : ''}.</p>`.value;
  }
}

const searchText = ({ vehicle: v, access, claim }) =>
  [access?.nickname, claim?.submitted?.nickname, v.brand, v.model, v.plate, v.plateFormatted, v.serialNumber, v.modelYear].filter(Boolean).join(' ').toLocaleLowerCase('pt-BR');

function row(it) {
  const { vehicle: v, access, claim, summary } = it;
  const unit = meterOf(v);
  const nickname = access?.nickname || claim?.submitted?.nickname;
  const model = [v.brand, v.model].filter(Boolean).join(' ');
  const title = nickname || vehicleName(v) || vehicleId(v);
  const id = vehicleId(v);
  const meter = summary?.currentKm !== null && summary?.currentKm !== undefined ? fmtKm(summary.currentKm, unit) : '';
  const sub = [nickname ? model : '', id, access ? meter : 'documento enviado'].filter(Boolean).join(' · ');
  const status = access
    ? (() => {
        const n = nextShort(summary?.next, unit);
        return summary?.next ? pill(n.cls || 'medium', n.cls === 'success' ? 'Em dia' : n.text) : access.relation !== 'OWNER' && RELATION[access.relation] ? pill(RELATION[access.relation].color, RELATION[access.relation].label) : '';
      })()
    : CLAIM_STATUS[claim?.status]
      ? pill(CLAIM_STATUS[claim.status].color, CLAIM_STATUS[claim.status].label)
      : '';
  return html`
    <button type="button" class="row tappable" data-go="/vehicles/${v.id}">
      ${catIcon(categoryOf(v).key)}
      <span class="row-main">
        <span class="row-title">${title}</span>
        <span class="row-sub one-line">${sub}</span>
      </span>
      ${status}
    </button>`;
}

customElements.define('page-garage', GaragePage);
